/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/anchor-is-valid */

import { BodyM, Caption, colorVariants, Description, Small } from '@casavo/base-ui'
import { capitalize } from 'lodash'
import useTranslation from 'next-translate/useTranslation'
import { useState, useEffect, memo, useRef } from 'react'
import ImageGallery from 'react-image-gallery'

import CardCasavoLogo from '@/components/CardCasavoLogo'
import ListingDetailLink from '@/components/Link/ListingDetailLink'
import Tooltip from '@/components/Tooltip'
import { useCities } from '@/context/Cities'
import { useDevice } from '@/context/Device'
import { Badge } from '@/design-lib/components/Badge'
import { sprinkles } from '@/design-lib/style-theme/sprinkles.css'
import { paddingBottom } from '@/shared-styles/styles.css'
import { Listings_Detail } from '@/types/cate-graphql-generated-types'
import { CasavoInventory } from '@/types/listing'
import fragmentCounter from '@/utils/balcony-counter'
import { fromNow } from '@/utils/date'
import { getFloor } from '@/utils/floors'
import { formatListingSellingPrice } from '@/utils/format-listing-sellingPrice'
import getTranslatedTitle from '@/utils/get-translated-title'
import { isStorageSupported } from '@/utils/isStorageSupported'
import { getRoomDataForLocale } from '@/utils/locale-variables'
import { checkListingNew, checkListingHasBeenVisited, checkListingPublishedIn } from '@/utils/new-property'
import {
  handleClick,
  handleListingPageClick,
  handleListingsMapClick,
  sendSupportEventWithListingId,
} from '@/utils/tracking/eventsHandlers'

import LazyLoadImageSkeleton from '../_base/LazyLoadImage'
import { FavouriteButton } from '../FavouriteButton'
import Icon from '../Icon'
import { ImageGalleryBullets } from '../ImageGalleryBullets'

import {
  cardBody,
  cardBodyOnMap,
  cardCityArea,
  cardCityAreaDivider,
  cardData,
  cardDataValue,
  cardImage,
  cardImageOnMap,
  cardInfoIcon,
  cardInfoRow,
  cardTitleInfo,
  listingCard,
  listingCardCarouselButton,
  listingCardTitle,
} from './styles.css'

type Props = {
  dataTestId?: string
  isLoading: boolean
  isOnMap: boolean
  listing: Listings_Detail
  onListingCardHover: (id: string) => void
}

const ListingCard = memo(({ dataTestId, isLoading, isOnMap, listing, onListingCardHover }: Props) => {
  const { lang, t } = useTranslation()
  const cities = useCities()
  const device = useDevice()
  const isIBuyerOrOdaInventory =
    listing.detail.inventory === CasavoInventory.iBuyer || listing.detail.inventory === CasavoInventory.Agency

  useEffect(() => {
    if (isLoading && isStorageSupported(() => sessionStorage) && sessionStorage.getItem('CASAVO@LISTING-BACK')) {
      setTimeout(() => sessionStorage.removeItem('CASAVO@LISTING-BACK'), 100)
    }
  }, [isLoading])

  const imageHeight = !isOnMap ? 200 : 170

  const images = listing.photos?.slice(0, 8).map((photo) => ({
    original: `${photo.url}?auto=compress,format&fit=crop&${isOnMap ? 'ar=1:1' : 'ar=2:1'}&h=${
      isOnMap ? '170' : '400'
    }`,
    primary: photo.isPrimary,
  }))

  const imageGalleryRef = useRef(null)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)

  const listingCity = cities.list.find((city) => city.slug === listing.detail.city)
  const isNew = checkListingNew(listing)
  const hasBeenPublishedInTheLast72Hours = checkListingPublishedIn(listing.publishedAt, 72)
  const hasBeenVisited = checkListingHasBeenVisited(listing.id)

  const handlerNavArrowsClick = (onClick) => (event) => {
    event.preventDefault()
    event.stopPropagation()
    onClick()
  }

  /** argument for getFloor() */
  const floorData = {
    buildingFloors: listing.detail.buildingFloors,
    entireBuilding: listing.detail.entireBuilding,
    entranceFloor: listing.detail.entranceFloor,
    floorPosition: listing.detail.floorPosition,
  }

  const { numberOfRooms, roomLabel } = getRoomDataForLocale(lang, listing.characteristics)

  return (
    <ListingDetailLink cityValue={listingCity.slug} slug={listing.slug} zoneSlug={listing?.detail.zone?.slug ?? ''}>
      <a
        data-testid={dataTestId}
        onClick={() => {
          isStorageSupported(() => sessionStorage) &&
            sessionStorage.setItem('CASAVO@LISTINGS-PAGE-OFFSET', `${window.pageYOffset}`)

          isOnMap ? handleListingsMapClick('ListingCard') : handleListingPageClick('ListingCard')

          sendSupportEventWithListingId(listing.id, isOnMap ? true : false)

          if (isNew && !isOnMap) handleListingsMapClick('NewListingCard')
        }}
      >
        <div
          className={listingCard({ isOnMap })}
          onMouseEnter={() => {
            if (device === 'desktop' && onListingCardHover) {
              onListingCardHover(listing.id)
            }
          }}
          onMouseLeave={() => {
            if (device === 'desktop' && onListingCardHover) {
              onListingCardHover(null)
            }
          }}
        >
          <div className={isOnMap ? cardImageOnMap : cardImage}>
            {listing.contactInfo.ownedByCasavo && (
              <CardCasavoLogo inventory={listing.detail.inventory as CasavoInventory} isLabelVisible={!isOnMap} />
            )}

            <ImageGallery
              ref={imageGalleryRef}
              items={images}
              renderCustomControls={
                isOnMap
                  ? null
                  : () => (
                      <ImageGalleryBullets
                        currentIndex={currentImageIndex}
                        totalItems={listing.photos.slice(0, 8).length}
                      />
                    )
              }
              renderItem={(item) => {
                return <LazyLoadImageSkeleton height={imageHeight} src={item.original} />
              }}
              renderLeftNav={(onClick) => {
                return !isOnMap && device === 'desktop' ? (
                  <button className={listingCardCarouselButton.prev} onClick={handlerNavArrowsClick(onClick)} />
                ) : null
              }}
              renderRightNav={(onClick) => {
                return !isOnMap && device === 'desktop' ? (
                  <button className={listingCardCarouselButton.next} onClick={handlerNavArrowsClick(onClick)} />
                ) : null
              }}
              showBullets={false}
              showFullscreenButton={false}
              showPlayButton={false}
              showThumbnails={false}
              slideDuration={300}
              stopPropagation={true}
              onSlide={() => {
                setCurrentImageIndex(imageGalleryRef.current.getCurrentIndex())
                handleClick('ListingCard', 'ChangeImage')
              }}
            />

            {isNew && !isOnMap && (
              <Badge
                className={sprinkles({
                  position: 'absolute',
                  right: 's',
                  top: 's',
                })}
                label={t('common:new_listing_label')}
                styleVariant="warning"
              />
            )}

            {listing.contactInfo.ownedByCasavo && !isOnMap && listing.virtualTours && listing.virtualTours.url && (
              <Badge
                className={sprinkles({
                  bottom: 'm-l',
                  left: 's',
                  position: 'absolute',
                })}
                icon="VirtualTour"
                label={t('common:cta_show_virtual_tour')}
                styleVariant="greyscale"
              />
            )}
          </div>
          <div className={isOnMap ? cardBodyOnMap : cardBody}>
            <div>
              <div className={cardTitleInfo}>
                <Caption className={cardCityArea}>
                  <span>{listingCity?.translatedCityName}</span>

                  {listing.detail.zone && (
                    <>
                      <span className={cardCityAreaDivider} />
                      <span data-testid="listing-card-zone">{listing.detail.zone.label}</span>
                    </>
                  )}
                </Caption>

                {!isOnMap && hasBeenPublishedInTheLast72Hours && (
                  <Small
                    className={sprinkles({
                      flexShrink: 0,
                      marginLeft: 's',
                    })}
                    color={hasBeenVisited ? 'greyscale500' : 'greyscale600'}
                  >
                    {capitalize(fromNow(listing.publishedAt, lang))}
                  </Small>
                )}
              </div>
              <BodyM strong className={isOnMap ? listingCardTitle : paddingBottom.s} data-testid="listing-card-title">
                {getTranslatedTitle(listing.detail?.titles, lang)}
              </BodyM>
              <div className={cardData}>
                <div className={cardDataValue}>
                  <Description>{listing.detail.commercialArea}</Description>
                  <Caption>m&sup2;</Caption>
                </div>
                {getFloor(floorData, t)}
              </div>
              <div className={cardInfoRow}>
                <div>
                  <Caption>{t(`common:${roomLabel}`)}</Caption>
                  <div className={cardDataValue}>
                    {numberOfRooms}
                    <Icon.Rooms className={cardInfoIcon} />
                  </div>
                </div>

                <div>
                  <Caption>{t('common:bathrooms')}</Caption>
                  <div className={cardDataValue}>
                    {listing.characteristics.bathrooms}
                    <Icon.Bathroom className={cardInfoIcon} />
                  </div>
                </div>
                <div>
                  <Caption>{t('common:status')}</Caption>
                  <div className={cardDataValue}>
                    {t(`common:${listing.characteristics.stateOfMaintenance.toLowerCase()}`)}
                  </div>
                </div>
              </div>
            </div>

            <div
              className={sprinkles({
                alignItems: 'center',
                display: 'flex',
                flexWrap: {
                  desktop: 'nowrap',
                  mobile: 'wrap',
                  tablet: 'wrap',
                },
                justifyContent: 'space-between',
              })}
            >
              {(fragmentCounter.get(listing.characteristics.terraces) > 0 || listing.services.garden) && (
                <div
                  className={sprinkles({
                    flexBasis: {
                      desktop: 'unset',
                      mobile: '100%',
                      tablet: '100%',
                    },
                  })}
                >
                  <Tooltip text={t('common:tooltips.outdoor_space')}>
                    <Badge
                      className={sprinkles({ marginRight: 'xs', maxWidth: '100%' })}
                      icon="Tree"
                      label={!isOnMap && t('common:outdoor_space_label')}
                      styleVariant="greyscale"
                    />
                  </Tooltip>
                </div>
              )}

              {(listing.detail.sellingPrices || listing.detail.privateNegotiation) && (
                <div
                  className={sprinkles({
                    alignItems: 'center',
                    display: 'flex',
                    flexGrow: { desktop: 0, mobile: 1, tablet: 1 },
                    gap: 's-m',
                    marginLeft: 'auto',
                  })}
                >
                  {isIBuyerOrOdaInventory && <Icon.Check className={colorVariants.orange600} />}
                  <BodyM color="greyscale600">
                    {formatListingSellingPrice(
                      listing.detail.privateNegotiation,
                      listing.detail.sellingPrices,
                      t,
                      lang
                    )}
                  </BodyM>
                </div>
              )}

              <FavouriteButton
                className={sprinkles({
                  padding: 's',
                })}
                //TODO: replace with a real value
                gtmTouchpoint={'ListingCard'}
                id={listing.id}
                isFavouriteInitialValue={false}
              />
            </div>
          </div>
        </div>
      </a>
    </ListingDetailLink>
  )
})

ListingCard.displayName = 'ListingCard'

export default ListingCard
